import anime from 'animejs';
import Image from 'next/image';
import Link from 'next/link';
import type { HTMLAttributes } from 'react';
import { useInViewEffect } from 'react-hook-inview';

import cn from '@/helper/cn';

import InfiniteScroll from '../components/infinite-scroll';
import type { InfiniteScrollType } from '../constants/solutions';

type HeroSectionType = {
  infiniteScrollItems: InfiniteScrollType[];
  infiniteScrollClassNames?: string;
  title: string;
  infoSummary: string;
  image: {
    src: string;
    alt: string;
    width?: number;
    height?: number;
  };
} & HTMLAttributes<HTMLDivElement>;

const HeroSection = ({
  infiniteScrollItems,
  infiniteScrollClassNames,
  className: sectionClassName,
  title,
  infoSummary,
  image,
  ...rest
}: HeroSectionType) => {
  const { src, alt, width = 624, height = 300 } = image;

  const sectionRef = useInViewEffect(
    ([entry], observer) => {
      if (entry.isIntersecting) {
        observer.unobserve(entry.target);

        anime
          .timeline({ loop: false })
          .add({
            targets: '#hero-section-description',
            scale: [0.9, 1],
            opacity: [0, 1],
            duration: 500,
            easing: 'easeOutExpo',
          })
          .add(
            {
              targets: '#hero-demo-request-button',
              scale: [0.9, 1],
              opacity: [0, 1],
              duration: 150,
              easing: 'easeOutQuad',
            },
            '-=400',
          )
          .add(
            {
              targets: '#hero-img',
              opacity: [0, 1],
              translateY: [100, 0],
              duration: 1000,
              easing: 'easeOutExpo',
            },
            '-=600',
          );
      }
    },
    { threshold: 0.2 },
  );

  return (
    <section
      id="hero-section"
      ref={sectionRef}
      className={cn(
        'relative isolate mx-auto flex w-full max-w-container flex-col items-center justify-end gap-y-10 overflow-hidden pt-24 md:pt-28 xl:gap-y-36 xl:pt-44',
        sectionClassName,
      )}
      {...rest}
    >
      <div className="flex w-full flex-col-reverse justify-between gap-y-10 px-10 pb-8 lg:flex-row lg:gap-x-8 lg:px-20 lg:pb-0 xl:gap-x-28">
        <div className="flex flex-col gap-6 text-left md:max-lg:items-center md:max-lg:text-center lg:max-xl:basis-3/4">
          <h1 className="text-2xl font-medium text-brand-600 sm:text-4xl md:text-5xl/tight">
            {title}
          </h1>
          <p
            id="hero-section-description"
            className="max-w-xl text-xs text-zinc-600 text-opacity-70 opacity-0 [text-wrap:balance] sm:text-lg md:text-xl"
          >
            {infoSummary}
          </p>
          <Link
            id="hero-demo-request-button"
            href="/contact-us"
            className="max-w-[122.5px] rounded-full bg-brand-600 px-3 py-[.33rem] text-center text-xs font-medium text-slate-50 opacity-0 duration-200 hover:bg-brand-500 focus:bg-brand-500 active:bg-brand-700 sm:max-w-[150px] sm:text-base md:flex md:justify-center lg:max-w-[200px] lg:px-8 lg:py-2 lg:text-base"
          >
            Request a Demo
          </Link>
        </div>
        <div id="hero-img" className="flex justify-center xl:shrink-0">
          <Image
            src={src}
            alt={alt}
            quality={100}
            priority={true}
            width={width}
            height={height}
          />
        </div>
      </div>
      <div className="relative isolate flex w-full overflow-hidden">
        <InfiniteScroll
          items={infiniteScrollItems}
          className={cn('hidden lg:inline-flex', infiniteScrollClassNames)}
        />
        <InfiniteScroll
          items={infiniteScrollItems}
          className={cn('lg:hidden', infiniteScrollClassNames)}
          isMobile={true}
        />
      </div>
    </section>
  );
};

export default HeroSection;
